// Projects builder working-state (+ tenant chrome) into the PassPreview prop
// shape (slice 4a-3). Kept out of ProgramWizard so it can be unit-tested and
// reused by 4a-4's enrollment page.

import {
  ALL_ONBOARDING_FIELDS,
  DEFAULT_MEMBER_NUMBER_LABEL,
  ONBOARDING_FIELD_LABELS,
  type EventTicketConfig,
  type OnboardingFieldKey,
} from '@naya/shared'

import { formatExpiry } from '../../shared/membership/membershipTypes.js'
import type {
  PassPreviewDetail,
  PassPreviewProgram,
  PassPreviewReward,
} from '../../shared/pass-preview/PassPreview.js'
import {
  grandPrize,
  isEventTicketDraft,
  isMembershipDraft,
  type WorkingDraft,
} from './builderState.js'

// A representative "valid until" date for the membership preview front, mirroring
// the device pass. Only non-null when the program expires (item 3 — "never
// expires" shows nothing). 'fixed' uses the owner's date; 'relative' projects
// from today (illustrative — the live per-card expiry is sign-up + period).
function representativeExpiry(draft: WorkingDraft): string | null {
  if (draft.expiration_mode === 'fixed') {
    if (!draft.expiration_fixed_date) return null
    return formatExpiry(`${draft.expiration_fixed_date}T23:59:59.999Z`)
  }
  if (draft.expiration_mode === 'relative') {
    const unit = draft.expiration_relative_unit
    const amount = draft.expiration_relative_amount
    if (!unit || !amount) return null
    const d = new Date()
    if (unit === 'days') d.setDate(d.getDate() + amount)
    else if (unit === 'months') d.setMonth(d.getMonth() + amount)
    else d.setFullYear(d.getFullYear() + amount)
    return formatExpiry(d.toISOString())
  }
  return null
}

// Dummy values for the pass-front detail row in the builder preview — the
// owner is designing a template, so there's no real customer yet. Names are
// stock PH placeholders ("Juan Dela Cruz") to make the preview feel local.
// `birthday` is rendered "Month Day" once we have a real value; in the
// preview we just show the formatted dummy.
const DUMMY_FIELD_VALUES: Record<OnboardingFieldKey, string> = {
  first_name: 'Juan',
  last_name: 'Dela Cruz',
  email: 'juan@email.com',
  birthday: 'September 15',
  phone: '09112223333',
}

/**
 * Build the PassPreview props from the working draft. `filledCount` defaults
 * to 0 (a brand-new card) — 4a-4's enrollment/card view will pass a real count.
 */
export function draftToPreview(
  draft: WorkingDraft,
  chrome: { businessName: string; logoUrl: string | null },
  filledCount = 0,
): PassPreviewProgram {
  // Detail row — only the owner-chosen fields that are still enabled. (A field
  // can be deselected from front_fields, or disabled in onboarding entirely;
  // we guard on both so a stale front_fields entry never renders a blank cell.)
  // first_name + last_name are excluded: both are carried by the "Member" name
  // ("Juan Dela Cruz"), so the live pass renders no separate row for either.
  const detailFields: PassPreviewDetail[] = draft.pass_design.front_fields
    .filter((key) => key !== 'first_name' && key !== 'last_name')
    .filter((key) => draft.onboarding_fields[key]?.enabled)
    .map((key) => ({
      label: ONBOARDING_FIELD_LABELS[key],
      value: DUMMY_FIELD_VALUES[key],
    }))

  // SLICE-MEMBERSHIP-PASS — a membership pass has no stamps/milestones/rewards.
  // Branch here so the loyalty projection below stays byte-identical. The
  // membership preview reuses the shared chrome (header, detail fields,
  // enroll/back content) and carries the membership-specific block instead of
  // the stamp grid + reward ladder.
  if (isMembershipDraft(draft)) {
    return draftToMembershipPreview(draft, chrome, detailFields)
  }

  // SLICE-EVENT-TICKET — an event ticket has no stamps/milestones either; it
  // shows event name / when / where / tier instead.
  if (isEventTicketDraft(draft)) {
    return draftToEventTicketPreview(draft, chrome)
  }

  // Rewards — the next unearned milestone and the grand prize. Milestones are
  // sorted by position so "next" is the first one past `filledCount`.
  const grand = grandPrize(draft.milestones)
  const sorted = [...draft.milestones].sort((a, b) => a.at_stamp - b.at_stamp)
  const next = sorted.find(
    (m) => m.type !== 'final' && m.at_stamp > filledCount,
  )

  const grandPrizeReward: PassPreviewReward = {
    label: `Grand prize · stamp ${grand?.at_stamp ?? draft.total_stamps}`,
    text: grand?.reward_text ?? '',
  }

  const result: PassPreviewProgram = {
    businessName: chrome.businessName,
    programName: draft.name,
    logoUrl: chrome.logoUrl,
    heroImageUrl: draft.pass_design.hero_original_url,
    heroBrightness: draft.pass_design.hero_brightness,
    totalStamps: draft.total_stamps,
    filledCount,
    primaryColor: draft.pass_design.primary_color,
    textColor: draft.pass_design.text_color,
    stampColor: draft.pass_design.stamp_color,
    stampFilledRef: draft.pass_design.stamp_filled_url,
    stampEmptyRef: draft.pass_design.stamp_empty_url,
    detailFields,
    grandPrize: grandPrizeReward,
    // All milestone stamp positions — both intermediates and the grand
    // prize. Empty slots at these positions get the star-badge decoration.
    milestoneStamps: draft.milestones.map((m) => m.at_stamp),
    // Secret menu — the builder has no real per-card stamp history, so this
    // is always empty. Same "not wired to real data yet" pattern as
    // `rewards`'s claimed-state note above.
    secretStamps: [],
    // Full reward ladder for the back-of-pass "All rewards" row — mirrors
    // the live wallet pass's `all-rewards` textModule. Sorted ascending so
    // the row reads top-to-bottom. The renderer falls back to a placeholder
    // when reward_text is still empty (a half-edited draft).
    rewards: [...draft.milestones]
      .sort((a, b) => a.at_stamp - b.at_stamp)
      .map((m) => ({ at_stamp: m.at_stamp, reward_text: m.reward_text })),
    pointsLabel: draft.pass_design.points_label,
    passDescription: draft.pass_design.pass_description,
    // Slice 4b — header "Member · Juan" pattern + back-of-pass content. Full
    // name when the program collects a last name ("Juan Dela Cruz"), else just
    // the first — same rule as the live pass + the membership preview.
    accountNameLabel: draft.pass_design.account_name_label,
    customerName: draft.onboarding_fields.last_name?.enabled
      ? `${DUMMY_FIELD_VALUES.first_name} ${DUMMY_FIELD_VALUES.last_name}`
      : DUMMY_FIELD_VALUES.first_name,
    customerEmail: DUMMY_FIELD_VALUES.email,
    backLinks: draft.back_links,
    ownerTerms: draft.owner_terms,
    // The enabled onboarding fields in canonical ALL_ONBOARDING_FIELDS order
    // so the enrollment-screen mock always lays them out the same way the
    // real enroll page does. Each entry's `required` flag drives the asterisk
    // marker in the mock; disabled fields are omitted entirely.
    enrollmentFields: ALL_ONBOARDING_FIELDS.filter(
      (key) => draft.onboarding_fields[key]?.enabled,
    ).map((key) => ({
      key,
      label: ONBOARDING_FIELD_LABELS[key],
      required: draft.onboarding_fields[key]?.required ?? false,
    })),
  }
  if (next) {
    result.nextMilestone = {
      label: `Next at stamp ${next.at_stamp}`,
      text: next.reward_text,
    }
  }

  // SLICE-REFERRAL — when the program runs a live referral, show a
  // representative back-of-pass referral block (the owner has no real card at
  // design time, so the code/counts are dummy placeholders). Mirrors the live
  // pass's referral textModules + share link. Configured on the owner Referrals
  // page, not the wizard — this projection just reflects an enabled config.
  const ref = draft.referral_config
  if (ref?.enabled) {
    // Mirrors buildReferralPassFields (apps/api) — two sections: a copyable
    // invite message + the mechanics/status board. Dummy link + zeroed counts
    // since the owner has no real card at design time.
    const bonusWord = ref.referrer_bonus_stamps === 1 ? 'stamp' : 'stamps'
    const business = chrome.businessName.trim() || 'us'
    result.referral = {
      textRows: [
        {
          label: 'Invite a friend (copy this)',
          value: [
            `Get a loyalty pass for ${business} with my link:`,
            '',
            'pass.naya.ph/r/NAYA1234',
            '',
            `Earn ${ref.referee_welcome_multiplier}x stamps on your first visit.`,
          ].join('\n'),
        },
        {
          label: 'Your referrals',
          value: [
            `They get ${ref.referee_welcome_multiplier}x stamps on their first visit.`,
            `You get ${ref.referrer_bonus_stamps} bonus ${bonusWord}, added automatically once they collect ${ref.referee_threshold_stamps} stamps.`,
            '',
            'Friends joined: 0',
            'In progress: 0',
            `Rewards earned: 0 of ${ref.max_referrals_per_person}`,
          ].join('\n'),
        },
      ],
      // Share link removed from the pass (see referral-fields.ts).
      shareUrl: null,
    }
  }

  return result
}

// Dummy member number + "member since" for the builder preview — the owner has
// no real member at design time, so these are stock placeholders mirroring the
// per-card values the live pass will carry.
const DUMMY_MEMBER_NUMBER = 'M-000123'
const DUMMY_MEMBER_SINCE = 'January 2026'

/**
 * SLICE-MEMBERSHIP-PASS — project a membership working draft into the
 * PassPreview prop shape. No stamps/milestones/rewards; the loyalty-only
 * fields are filled with inert defaults (never rendered because
 * `passType: 'membership'` suppresses them) so the single PassPreviewProgram
 * shape serves both pass types.
 */
function draftToMembershipPreview(
  draft: WorkingDraft,
  chrome: { businessName: string; logoUrl: string | null },
  _detailFields: PassPreviewDetail[],
): PassPreviewProgram {
  // Only reached for a membership draft, so type_config is a MembershipConfig.
  const config = draft.type_config as
    | import('@naya/shared').MembershipConfig
    | null
  const inertReward: PassPreviewReward = { label: '', text: '' }
  // Member name mirrors the live pass: full name when the owner collects a last
  // name ("Juan Dela Cruz"), else just the first ("Juan").
  const memberName = draft.onboarding_fields.last_name?.enabled
    ? `${DUMMY_FIELD_VALUES.first_name} ${DUMMY_FIELD_VALUES.last_name}`
    : DUMMY_FIELD_VALUES.first_name

  return {
    businessName: chrome.businessName,
    programName: draft.name,
    logoUrl: chrome.logoUrl,
    heroImageUrl: draft.pass_design.hero_original_url,
    heroBrightness: draft.pass_design.hero_brightness,
    // Loyalty-only fields — inert defaults; the membership renderer ignores
    // them (no stamp grid, no reward rows).
    totalStamps: 0,
    filledCount: 0,
    milestoneStamps: [],
    // Secret menu — not wired to real data yet (no stamp grid on these pass
    // types either); always empty.
    secretStamps: [],
    grandPrize: inertReward,
    rewards: [],
    primaryColor: draft.pass_design.primary_color,
    textColor: draft.pass_design.text_color,
    stampColor: draft.pass_design.stamp_color,
    stampFilledRef: draft.pass_design.stamp_filled_url,
    stampEmptyRef: draft.pass_design.stamp_empty_url,
    // A membership pass renders NO arbitrary front_field rows (the device shows
    // only member name + tier + number + expiry), and the last name is already
    // folded into the member name — so the detail row is intentionally empty.
    detailFields: [],
    pointsLabel: draft.pass_design.points_label,
    passDescription: draft.pass_design.pass_description,
    accountNameLabel: draft.pass_design.account_name_label,
    customerName: memberName,
    customerEmail: DUMMY_FIELD_VALUES.email,
    backLinks: draft.back_links,
    ownerTerms: draft.owner_terms,
    enrollmentFields: ALL_ONBOARDING_FIELDS.filter(
      (key) => draft.onboarding_fields[key]?.enabled,
    ).map((key) => ({
      key,
      label: ONBOARDING_FIELD_LABELS[key],
      required: draft.onboarding_fields[key]?.required ?? false,
    })),
    // The membership-specific content — drives the front tier/number row and
    // the back benefits list.
    passType: 'membership',
    membership: {
      // Tiers on → preview a representative tier (the first non-empty option);
      // off → no tier line. The owner has no real member at design time.
      tierName: config?.tiers_enabled
        ? (config.tiers.find((t) => t.trim().length > 0)?.trim() ?? null)
        : null,
      memberNumberLabel:
        config?.member_number_label ?? DEFAULT_MEMBER_NUMBER_LABEL,
      memberNumber: DUMMY_MEMBER_NUMBER,
      memberSince: DUMMY_MEMBER_SINCE,
      // Front + back expiry, full date — null when the program never expires.
      validUntil: representativeExpiry(draft),
      benefits: config?.benefits ?? [],
    },
  }
}

const DUMMY_TICKET_NUMBER = 'NAY-2J5K-9P3M'

/** PH-local event time for the preview, mirroring the device pass. */
function previewEventWhen(startsAt: string | undefined): string {
  if (!startsAt) return 'Event date & time'
  const t = Date.parse(startsAt)
  if (Number.isNaN(t)) return 'Event date & time'
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(t))
}

/**
 * SLICE-EVENT-TICKET — project an event working draft into the PassPreview prop
 * shape. Loyalty-only fields are inert (suppressed by passType 'event_ticket').
 */
function draftToEventTicketPreview(
  draft: WorkingDraft,
  chrome: { businessName: string; logoUrl: string | null },
): PassPreviewProgram {
  const config = draft.type_config as EventTicketConfig | null
  const inertReward: PassPreviewReward = { label: '', text: '' }
  const firstTier = config?.tiers?.[0]

  return {
    businessName: chrome.businessName,
    programName: draft.name,
    logoUrl: chrome.logoUrl,
    heroImageUrl: draft.pass_design.hero_original_url,
    heroBrightness: draft.pass_design.hero_brightness,
    totalStamps: 0,
    filledCount: 0,
    milestoneStamps: [],
    // Secret menu — not wired to real data yet (no stamp grid on these pass
    // types either); always empty.
    secretStamps: [],
    grandPrize: inertReward,
    rewards: [],
    primaryColor: draft.pass_design.primary_color,
    textColor: draft.pass_design.text_color,
    stampColor: draft.pass_design.stamp_color,
    stampFilledRef: draft.pass_design.stamp_filled_url,
    stampEmptyRef: draft.pass_design.stamp_empty_url,
    detailFields: [],
    pointsLabel: draft.pass_design.points_label,
    passDescription: draft.pass_design.pass_description,
    accountNameLabel: draft.pass_design.account_name_label,
    customerName: DUMMY_FIELD_VALUES.first_name,
    customerEmail: DUMMY_FIELD_VALUES.email,
    backLinks: draft.back_links,
    ownerTerms: draft.owner_terms,
    enrollmentFields: ALL_ONBOARDING_FIELDS.filter(
      (key) => draft.onboarding_fields[key]?.enabled,
    ).map((key) => ({
      key,
      label: ONBOARDING_FIELD_LABELS[key],
      required: draft.onboarding_fields[key]?.required ?? false,
    })),
    passType: 'event_ticket',
    eventTicket: {
      eventName: config?.event_name?.trim() || 'Event name',
      whenText: previewEventWhen(config?.starts_at),
      venueName: config?.venue_name?.trim() || 'Venue',
      tierName: firstTier?.name?.trim() || 'General Admission',
      // Preview is a template (no real ticket) → show as general admission.
      seatText: null,
      ticketNumber: DUMMY_TICKET_NUMBER,
    },
  }
}
